import {
  AfterViewInit, Component, ElementRef, HostListener, Input, NgZone, OnDestroy, OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { BannerNotificationsService } from '../../../xform-compat';
import { ReleaseNoteService } from '../../../core/data-services/release-note.service';
import {
  MediaScope, PreviewLens, ReleaseNote, ReleaseNotePage, ReleaseNotePageMedia
} from '../../../shared/models/release-note/release-note.model';
import { ReleaseCover } from '../cover/release-cover.component';
import { ReleaseNoteMediaCacheService } from '../services/release-note-media-cache.service';
import { isListPage } from '../utils/release-note-caps.util';
import { FULL_MEDIA_MAX_HEIGHT_PX, isBesideWideBand, isVideoMedia, ratioOf, resolveLayout } from '../utils/release-note-layout.util';
import { codesOf } from '../utils/release-note-permission.util';
import { isPageVisibleTo } from '../utils/release-note-visibility.util';

export type ReleaseCardContext = 'splash' | 'library' | 'preview';

/** NgbModal options every opener uses, so the card is opened the same way from all three surfaces. */
export const RELEASE_CARD_MODAL_OPTIONS = {
  windowClass: 'release-card-modal',
  centered: true,
  backdrop: true,
  keyboard: true
} as const;

const SWIPE_THRESHOLD_PX = 45;
let nextCardId = 0;

/**
 * The one release card (RA-2695) — the splash, the Library and the editor's Preview all open this in an
 * NgbModal, which supplies the focus trap, focus return, Escape and `aria-modal`.
 *
 * Two screens: the **cover** (`rms-release-cover`) and the **slides** walkthrough, whose pager is built
 * from the pages it was given — already permission-filtered by the server, or by the Preview-as lens — so
 * numbering never has gaps. Media loads lazily: the cover eagerly, then the current slide's media and the
 * next slide's as a prefetch, never every slide at once.
 */
@Component({
  selector: 'rms-release-card',
  templateUrl: './release-card.component.html',
  styleUrls: ['./release-card.component.scss'],
  standalone: false
})
export class ReleaseCardComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() public context: ReleaseCardContext = 'library';
  @Input() public cover: ReleaseCover;
  /** The release to fetch when slides are needed and {@link release} was not supplied. */
  @Input() public releaseId: number;
  /** The release with its pages; fetched lazily on "See what's new" when absent. */
  @Input() public release: ReleaseNote | null = null;
  @Input() public startAt: 'cover' | number = 'cover';
  @Input() public mediaScope: MediaScope = 'public';
  /** Preview only: the personas to filter pages by. The first is the default. */
  @Input() public lensOptions: Array<PreviewLens> = [];

  public screen: 'cover' | 'slides' = 'cover';
  public index = 0;
  public mediaIndex = 0;
  public pages: Array<ReleaseNotePage> = [];
  public lensIndex = 0;
  public loadingPages = false;
  public coverUrl: string | null = null;
  public mediaUrls: { [ref: string]: string | null } = {};
  public failed: { [ref: string]: boolean } = {};
  public lightbox: { url: string | null; isVideo: boolean; alt: string } | null = null;
  public reducedMotion = false;
  public readonly labelId = `rx-version-${++nextCardId}`;
  public readonly titleId = `rx-title-${nextCardId}`;
  public readonly fullMaxHeight = FULL_MEDIA_MAX_HEIGHT_PX;

  private readonly requestedRefs = new Set<string>();
  private touchStart: { x: number; y: number } | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly onTouchStart = (event: TouchEvent) => this.handleTouchStart(event);
  private readonly onTouchEnd = (event: TouchEvent) => this.handleTouchEnd(event);

  constructor(
    public activeModal: NgbActiveModal,
    private readonly releaseNoteService: ReleaseNoteService,
    private readonly mediaCache: ReleaseNoteMediaCacheService,
    private readonly bannerNotificationsService: BannerNotificationsService,
    private readonly host: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone
  ) {}

  public ngOnInit(): void {
    this.reducedMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mediaCache.url(this.cover?.coverS3Reference, this.mediaScope)
      .pipe(takeUntil(this.destroy$))
      .subscribe(url => this.coverUrl = url);
    this.rebuildPages();
    if (typeof this.startAt === 'number') {
      this.openSlides(this.startAt);
    }
  }

  public ngAfterViewInit(): void {
    this.syncLabel();
    this.ngZone.runOutsideAngular(() => {
      this.host.nativeElement.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.host.nativeElement.addEventListener('touchend', this.onTouchEnd, { passive: true });
    });
  }

  public ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('touchstart', this.onTouchStart);
    this.host.nativeElement.removeEventListener('touchend', this.onTouchEnd);
    // Preview shares the editor's authoring-scope URLs, which the editor releases itself. The cover is never
    // released here: the Library hero or a sleeve may be showing the very same object URL.
    if (this.context !== 'preview') {
      this.mediaCache.revoke(this.mediaScope, Array.from(this.requestedRefs));
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** "See what's new" — one condition on every surface. */
  public get canSeeWhatsNew(): boolean {
    return this.context === 'preview' ? this.pages.length > 0 : (this.cover?.visiblePageCount ?? 0) > 0;
  }

  public get secondaryKey(): string {
    return this.context === 'splash' ? 'RELEASE_NOTES.CARD.CONTINUE' : 'RELEASE_NOTES.CARD.CLOSE';
  }

  public get currentPage(): ReleaseNotePage | null {
    return this.pages[this.index] ?? null;
  }

  public get currentMedia(): ReleaseNotePageMedia | null {
    return this.currentPage?.media?.[this.mediaIndex] ?? null;
  }

  public get isLastSlide(): boolean {
    return this.index >= this.pages.length - 1;
  }

  /** The lens's audience note, as a translate key and params. */
  public get lensNote(): { key: string; params: object } | null {
    const lens = this.lensOptions[this.lensIndex];
    if (!lens?.held) {
      return null;
    }
    return this.pages.length
      ? { key: 'RELEASE_NOTES.PREVIEW.SEES_N_OF', params: { n: this.pages.length, total: this.release?.pages?.length ?? 0 } }
      : { key: 'RELEASE_NOTES.PREVIEW.SEES_NONE', params: {} };
  }

  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    const inControl = !!target?.closest?.('input, select, textarea, [contenteditable="true"]');
    if (this.screen !== 'slides' || this.lightbox || inControl) {
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  }

  /** Opens the walkthrough, fetching the release first when only the cover was supplied. */
  public seeWhatsNew(): void {
    this.openSlides(0);
  }

  public prev(): void {
    if (this.index === 0) {
      this.screen = 'cover';
      this.syncLabel();
      return;
    }
    this.goTo(this.index - 1);
  }

  public next(): void {
    if (!this.isLastSlide) {
      this.goTo(this.index + 1);
    }
  }

  public goTo(index: number): void {
    this.index = index;
    this.mediaIndex = 0;
    this.screen = 'slides';
    this.lightbox = null;
    this.loadMedia(index);
    this.loadMedia(index + 1);
    this.syncLabel();
  }

  public showMedia(index: number): void {
    this.mediaIndex = index;
  }

  public setLens(index: number): void {
    this.lensIndex = Number(index);
    this.rebuildPages();
    if (this.screen === 'slides') {
      if (this.pages.length) {
        this.goTo(Math.min(this.index, this.pages.length - 1));
      } else {
        this.screen = 'cover';
        this.syncLabel();
      }
    }
  }

  /** The media pane's keyboard twin of its click; keys bubbling from its own buttons are theirs. */
  public onMediaKeydown(event: Event): void {
    if (event.target === event.currentTarget) {
      event.preventDefault();
      this.openLightbox();
    }
  }

  public openLightbox(): void {
    const media = this.currentMedia;
    if (!media) {
      return;
    }
    this.lightbox = {
      url: this.mediaUrls[media.s3Reference] ?? null,
      isVideo: this.isVideo(media),
      alt: this.currentPage?.mediaAlt ?? ''
    };
  }

  /** Closes the lightbox and puts focus back where it came from, so the next Escape still reaches the card. */
  public closeLightbox(): void {
    this.lightbox = null;
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>('.rx-slide__expand')?.focus());
  }

  public close(): void {
    this.activeModal.close();
  }

  public isVideo(media: ReleaseNotePageMedia | null | undefined): boolean {
    return isVideoMedia(media);
  }

  /** A looping clip autoplays muted — unless the user asked for reduced motion, when every clip waits. */
  public autoplays(page: ReleaseNotePage): boolean {
    return page.loop !== false && !this.reducedMotion;
  }

  public hasMediaPane(page: ReleaseNotePage): boolean {
    return !!page.media?.length || !!page.mediaAlt;
  }

  /** The body's layout class for a page. The first media decides, so cycling never reflows the slide. */
  public bodyClass(page: ReleaseNotePage): string {
    if (!this.hasMediaPane(page)) {
      return isListPage(page) ? 'rx-slide__body--list' : 'rx-slide__body--text';
    }
    const first = page.media?.[0];
    if (!first) {
      return 'rx-slide__body--full';
    }
    if (resolveLayout(first, page.mediaLayout) === 'full') {
      return 'rx-slide__body--full';
    }
    return isBesideWideBand(first, page.mediaLayout) ? 'rx-slide__body--beside rx-slide__body--band' : 'rx-slide__body--beside';
  }

  /** CSS `aspect-ratio` from the stored dimensions, so the box is sized before any byte arrives. */
  public aspectRatio(media: ReleaseNotePageMedia | null): string | null {
    return ratioOf(media) === null ? null : `${media.width} / ${media.height}`;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  private openSlides(index: number): void {
    if (this.release || this.context === 'preview') {
      if (this.pages.length) {
        this.goTo(Math.max(0, Math.min(index, this.pages.length - 1)));
      }
      return;
    }
    this.loadingPages = true;
    this.releaseNoteService.getRelease(this.releaseId)
      .pipe(finalize(() => this.loadingPages = false), takeUntil(this.destroy$))
      .subscribe({
        next: release => {
          this.release = release;
          this.rebuildPages();
          if (this.pages.length) {
            this.goTo(Math.max(0, Math.min(index, this.pages.length - 1)));
          } else {
            // Nothing visible any more (permissions changed since the list was read): hide the dead CTA.
            this.cover = { ...this.cover, visiblePageCount: 0 };
          }
        },
        error: message => this.bannerNotificationsService.error(message)
      });
  }

  private rebuildPages(): void {
    const all = this.release?.pages ?? [];
    const lens = this.context === 'preview' ? this.lensOptions[this.lensIndex] : null;
    this.pages = lens?.held
      ? all.filter(page => isPageVisibleTo(codesOf(page.permissions), lens.held))
      : [...all];
  }

  private loadMedia(index: number): void {
    for (const media of this.pages[index]?.media ?? []) {
      const ref = media.s3Reference;
      if (!ref || this.requestedRefs.has(ref)) {
        continue;
      }
      this.requestedRefs.add(ref);
      this.mediaCache.url(ref, this.mediaScope)
        .pipe(takeUntil(this.destroy$))
        .subscribe(url => this.mediaUrls[ref] = url);
    }
  }

  /** Labels the dialog by the version on the cover and by the title on slides. */
  private syncLabel(): void {
    const dialog = this.host.nativeElement.closest('[role="dialog"]');
    dialog?.setAttribute('aria-labelledby', this.screen === 'cover' ? this.labelId : this.titleId);
  }

  private handleTouchStart(event: TouchEvent): void {
    const touch = event.touches[0];
    const onBody = (event.target as Element)?.closest?.('.rx-slide__body');
    this.touchStart = touch && onBody ? { x: touch.clientX, y: touch.clientY } : null;
  }

  private handleTouchEnd(event: TouchEvent): void {
    const start = this.touchStart;
    this.touchStart = null;
    const touch = event.changedTouches[0];
    if (!start || !touch || this.screen !== 'slides' || this.lightbox) {
      return;
    }
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy) * 1.5) {
      this.ngZone.run(() => dx < 0 ? this.next() : this.prev());
    }
  }
}
